import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MaintenanceService, MaintenanceMaterialPayload, MaintenanceProgressPayload, ServiceCallPhotoDto, ServiceCallReportPdfDto } from '../../../Services/maintenance.service';
import { CustomerService } from '../../../Services/customer.service';
import { UserService } from '../../../Services/user.service';
import { Maintenance, MaintenanceStatus, MaintenanceRecurrenceType, MaintenanceMaterial, MaintenanceRegieLine, User, ProblemEntry } from '../../../Models/maintenance.model';
import { MaintenanceChecklist } from '../../../Models/maintenance-checklist.model';
import { ChecklistItem, ChecklistItemComment } from '../../../Models/checklist-item.model';
import { Customer, CustomerMaterialItem } from '../../../Models/customer.model';
import { ServiceReportDialogComponent } from '../shared/service-report-dialog/service-report-dialog.component';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { MaintenanceFormComponent } from '../maintenance-form/maintenance-form.component';
import { environment } from '../../../environments/environment';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

interface StoredServicePdf {
  fileName: string;
  pdfBase64?: string;
  generatedAt: string;
  serverUrl?: string;
}

export interface ServiceCallLocalData {
  description?: string;
  workItems?: string[];
  durationHours?: number;
  durationMinutes?: number;
  travelHours?: number;
  travelMinutes?: number;
  arrivalTime?: string;
  departureTime?: string;
  departureFromOffice?: string;
  returnToOffice?: string;
  initialSituation?: string;
  problems?: ProblemEntry[];
  customerSigneeName?: string;
  signatureDataUrl?: string;
  techSigneeName?: string;
  techSignatureDataUrl?: string;
  customerSignatureEnabled?: boolean;
  regieLines?: MaintenanceRegieLine[];
  photos?: { id?: number; dataUrl?: string; url?: string; name: string }[];
}

@Component({
  selector: 'app-maintenance-detail',
  templateUrl: './maintenance-detail.component.html',
  styleUrls: ['./maintenance-detail.component.css']
})
export class MaintenanceDetailComponent implements OnInit {
  maintenance: Maintenance | null = null;
  customer: Customer | null = null;
  loading = true;

  // Expose enum to template
  readonly MaintenanceStatus = MaintenanceStatus;

  // New checklist
  showNewChecklist = false;
  newChecklistName = '';

  // New checklist item
  activeChecklistId: number | null = null;
  newItemText = '';

  // Checklist templates (Vorlagen)
  templates: MaintenanceChecklist[] = [];

  // Checklist UX state
  collapsedChecklistIds = new Set<number>();
  remarksOpenItemIds = new Set<number>();
  private progressUpdate$ = new Subject<void>();

  // New material
  showMaterialForm = false;
  newMaterial: Partial<MaintenanceMaterialPayload> = { name: '', quantity: 1, unit: 'Stk', unitPrice: undefined };

  // Progress notes
  showProgressForm = false;
  progressNotes = '';

  // Duration tracking for service calls
  durationHours: number | null = null;
  durationMinutes: number | null = null;
  travelHours: number | null = null;
  travelMinutes: number | null = null;
  arrivalTime: string | null = null;
  departureTime: string | null = null;
  departureFromOffice: string | null = null;
  returnToOffice: string | null = null;
  regieHours: number | null = null;
  regieMinutes: number | null = null;

  // Service call local data (photos, signature, description)
  serviceCallData: ServiceCallLocalData | null = null;
  serverPhotos: ServiceCallPhotoDto[] = [];
  selectedPhotoIndex: number | null = null;
  sendingPdfEmail = false;
  hasStoredServicePdf = false;
  reportVersions: ServiceCallReportPdfDto[] = [];
  private latestGeneratedPdfBase64: string | null = null;
  users: User[] = [];

  // Photo adding
  @ViewChild('addPhotoInput') addPhotoInput!: ElementRef<HTMLInputElement>;
  @ViewChild('addPhotoInputPlanned') addPhotoInputPlanned!: ElementRef<HTMLInputElement>;

  // Signature editing
  @ViewChild('editSigCanvas') editSigCanvas!: ElementRef<HTMLCanvasElement>;
  showSignatureEditor = false;
  savingSignature = false;
  editSigneeName = '';
  private sigCtx: CanvasRenderingContext2D | null = null;
  private sigDrawing = false;
  sigEdited = false;
  private sigCanvasInit = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private maintenanceService: MaintenanceService,
    private customerService: CustomerService,
    private userService: UserService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const id = +params['id'];
      if (id) this.loadMaintenance(id);
    });
    this.userService.getUsers().subscribe(u => this.users = u ?? []);
    this.loadTemplates();
    // Fortschritts-Updates bündeln statt bei jedem Klick zu senden.
    this.progressUpdate$.pipe(debounceTime(1500)).subscribe(() => this.pushChecklistProgress());
  }

  /** Erledigte Checklisten beim Laden einklappen, offene aufgeklappt lassen. */
  private initChecklistCollapse(): void {
    this.collapsedChecklistIds.clear();
    for (const cl of this.checklists) {
      if ((cl.items?.length ?? 0) > 0 && this.getChecklistCompletionPercent(cl) === 100) {
        this.collapsedChecklistIds.add(cl.id);
      }
    }
  }

  isChecklistCollapsed(cl: MaintenanceChecklist): boolean {
    return this.collapsedChecklistIds.has(cl.id);
  }

  onChecklistExpandedChange(cl: MaintenanceChecklist, expanded: boolean): void {
    if (expanded) this.collapsedChecklistIds.delete(cl.id);
    else this.collapsedChecklistIds.add(cl.id);
  }

  isRemarksOpen(item: ChecklistItem): boolean {
    return this.remarksOpenItemIds.has(item.id)
      || !!(item.remarks && item.remarks.trim())
      || (item.comments?.length ?? 0) > 0;
  }

  toggleRemarks(item: ChecklistItem): void {
    if (this.remarksOpenItemIds.has(item.id)) this.remarksOpenItemIds.delete(item.id);
    else this.remarksOpenItemIds.add(item.id);
  }

  // --- Checklist item comments (mehrere Kommentare pro Prüfpunkt) ---
  newCommentTexts: { [itemId: number]: string } = {};

  addItemComment(item: ChecklistItem): void {
    const text = (this.newCommentTexts[item.id] ?? '').trim();
    if (!text) return;
    this.maintenanceService.addChecklistItemComment(item.id, text).subscribe({
      next: (created) => {
        item.comments = item.comments ?? [];
        item.comments.push(created);
        this.newCommentTexts[item.id] = '';
      },
      error: () => this.snackBar.open('Kommentar konnte nicht gespeichert werden', 'OK', { duration: 3000 })
    });
  }

  deleteItemComment(item: ChecklistItem, comment: ChecklistItemComment): void {
    this.maintenanceService.deleteChecklistItemComment(item.id, comment.id).subscribe({
      next: () => {
        item.comments = (item.comments ?? []).filter(c => c.id !== comment.id);
      },
      error: () => this.snackBar.open('Kommentar konnte nicht gelöscht werden', 'OK', { duration: 3000 })
    });
  }

  private pushChecklistProgress(): void {
    if (!this.maintenance) return;
    const newProgress = this.checklistProgress;
    if (newProgress !== this.maintenance.progressPercent) {
      this.maintenanceService.addProgressUpdate(this.maintenance.id, {
        progressPercent: newProgress,
        notes: `Checkliste: ${this.completedChecklistItems}/${this.totalChecklistItems} erledigt`
      }).subscribe(() => {
        if (this.maintenance) this.maintenance.progressPercent = newProgress;
      });
    }
  }

  // --- Checklist Templates (Vorlagen) ---
  loadTemplates(): void {
    this.maintenanceService.getChecklistTemplates().subscribe({
      next: t => this.templates = t ?? [],
      error: () => this.templates = []
    });
  }

  /** Wendet eine Vorlage an: legt eine neue Checkliste mit den Vorlagen-Punkten an. */
  applyTemplate(template: MaintenanceChecklist): void {
    if (!this.maintenance) return;
    this.maintenanceService.assignTemplateToMaintenance(template.id, this.maintenance.id, { name: template.name ?? undefined }).subscribe({
      next: (created) => {
        // Lokal anhängen statt Seite neu zu laden.
        if (this.maintenance) {
          this.maintenance.checklists = this.maintenance.checklists ?? [];
          if (!created.items) created.items = [];
          this.maintenance.checklists.push(created);
        }
        this.snackBar.open('Vorlage angewendet', 'OK', { duration: 2500 });
      },
      error: () => this.snackBar.open('Vorlage konnte nicht angewendet werden', 'OK', { duration: 3000 })
    });
  }

  /** Speichert die aktuelle Checkliste als wiederverwendbare Vorlage. */
  saveChecklistAsTemplate(cl: MaintenanceChecklist): void {
    const name = window.prompt('Name der Vorlage:', cl.name ?? '');
    if (name === null) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    this.maintenanceService.convertChecklistToTemplate(cl.id, { name: trimmed }).subscribe({
      next: () => {
        this.snackBar.open('Als Vorlage gespeichert', 'OK', { duration: 2500 });
        this.loadTemplates();
      },
      error: () => this.snackBar.open('Vorlage konnte nicht gespeichert werden', 'OK', { duration: 3000 })
    });
  }

  loadMaintenance(id: number): void {
    this.loading = true;
    this.hasStoredServicePdf = false;
    this.maintenanceService.getMaintenance(id).subscribe({
      next: (m) => {
        this.maintenance = m;
        this.initChecklistCollapse();
        this.durationHours = m.durationHours ?? null;
        this.durationMinutes = m.durationMinutes ?? null;
        this.travelHours = m.travelHours ?? null;
        this.travelMinutes = m.travelMinutes ?? null;
        this.arrivalTime = m.arrivalTime ?? null;
        this.departureTime = m.departureTime ?? null;
        this.departureFromOffice = m.departureFromOffice ?? null;
        this.returnToOffice = m.returnToOffice ?? null;
        this.regieHours = m.regieHours ?? null;
        this.regieMinutes = m.regieMinutes ?? null;
        this.loading = false;
        // Load customer info
        if (m.equipment?.customerId) {
          this.customerService.getCustomer(m.equipment.customerId).subscribe(c => this.customer = c);
        }
        // Load photos for all maintenance types
        this.loadPersistedPhotos(id);
        // Gespeicherte Report-Versionen + zuletzt am Server gespeicherten Bericht
        // für ALLE Wartungstypen laden (damit auch geplante Wartungen den
        // gespeicherten Bericht herunterladen können, ohne neu zu generieren).
        this.loadReportVersions(id);
        this.loadLatestServerReportPdf(id);
        // Load service call data from localStorage
        if (!this.isPlanned) {
          this.loadServiceCallData(id);
        }
      },
      error: () => {
        this.snackBar.open('Auftrag nicht gefunden', 'OK', { duration: 4000 });
        this.router.navigate(['/wartung']);
      }
    });
  }

  private loadServiceCallData(maintenanceId: number): void {
    try {
      const raw = localStorage.getItem(`service-call-${maintenanceId}`);
      if (raw) {
        this.serviceCallData = JSON.parse(raw);
        if (this.serviceCallData?.durationHours != null) this.durationHours = this.serviceCallData.durationHours;
        if (this.serviceCallData?.durationMinutes != null) this.durationMinutes = this.serviceCallData.durationMinutes;
        if (this.serviceCallData?.travelHours != null) this.travelHours = this.serviceCallData.travelHours;
        if (this.serviceCallData?.travelMinutes != null) this.travelMinutes = this.serviceCallData.travelMinutes;
        if (this.serviceCallData?.arrivalTime) this.arrivalTime = this.serviceCallData.arrivalTime;
        if (this.serviceCallData?.departureTime) this.departureTime = this.serviceCallData.departureTime;
        if (this.serviceCallData?.departureFromOffice) this.departureFromOffice = this.serviceCallData.departureFromOffice;
        if (this.serviceCallData?.returnToOffice) this.returnToOffice = this.serviceCallData.returnToOffice;
      }

      if (!this.serviceCallData) {
        this.serviceCallData = {};
      }

      if (!this.serviceCallData.workItems?.length && this.maintenance?.workItems?.length) {
        this.serviceCallData.workItems = [...this.maintenance.workItems];
      }
      if (this.maintenance?.regieLines?.length) {
        this.serviceCallData.regieLines = this.cloneRegieLines(this.maintenance.regieLines);
      }

      if ((this.serviceCallData.durationHours == null) && this.maintenance?.durationHours != null) {
        this.serviceCallData.durationHours = this.maintenance.durationHours;
      }
      if ((this.serviceCallData.durationMinutes == null) && this.maintenance?.durationMinutes != null) {
        this.serviceCallData.durationMinutes = this.maintenance.durationMinutes;
      }
      if ((this.serviceCallData.travelHours == null) && this.maintenance?.travelHours != null) {
        this.serviceCallData.travelHours = this.maintenance.travelHours;
      }
      if ((this.serviceCallData.travelMinutes == null) && this.maintenance?.travelMinutes != null) {
        this.serviceCallData.travelMinutes = this.maintenance.travelMinutes;
      }
      if (!this.serviceCallData.arrivalTime && this.maintenance?.arrivalTime) {
        this.serviceCallData.arrivalTime = this.maintenance.arrivalTime;
      }
      if (!this.serviceCallData.departureTime && this.maintenance?.departureTime) {
        this.serviceCallData.departureTime = this.maintenance.departureTime;
      }
      if (!this.serviceCallData.departureFromOffice && this.maintenance?.departureFromOffice) {
        this.serviceCallData.departureFromOffice = this.maintenance.departureFromOffice;
      }
      if (!this.serviceCallData.returnToOffice && this.maintenance?.returnToOffice) {
        this.serviceCallData.returnToOffice = this.maintenance.returnToOffice;
      }
      if (!this.serviceCallData.initialSituation && this.maintenance?.initialSituation) {
        this.serviceCallData.initialSituation = this.maintenance.initialSituation;
      }
      if (!this.serviceCallData.problems?.length && this.maintenance?.problems?.length) {
        this.serviceCallData.problems = [...this.maintenance.problems];
      }
    } catch (e) {
      console.warn('Could not load service call data from localStorage:', e);
    }
  }

  reload(): void {
    if (this.maintenance) this.loadMaintenance(this.maintenance.id);
  }

  get isPlanned(): boolean {
    return !!this.maintenance && this.maintenance.recurrenceType !== MaintenanceRecurrenceType.None;
  }

  get isCompleted(): boolean {
    return this.maintenance?.status === MaintenanceStatus.Completed;
  }

  get checklists(): MaintenanceChecklist[] {
    return this.maintenance?.checklists ?? [];
  }

  get materials(): MaintenanceMaterial[] {
    return this.maintenance?.materials ?? [];
  }

  get customerMaterials(): CustomerMaterialItem[] {
    return this.customer?.materialItems ?? [];
  }

  get hourlyRates() {
    return this.customer?.hourlyRates ?? [];
  }

  get totalChecklistItems(): number {
    return this.checklists.reduce((s, cl) => s + (cl.items?.length ?? 0), 0);
  }

  get completedChecklistItems(): number {
    // "Nicht OK" zählt als bearbeitet – der Punkt wurde geprüft, Ergebnis negativ.
    return this.checklists.reduce((s, cl) => s + (cl.items?.filter(i => i.isChecked || i.isNotOk)?.length ?? 0), 0);
  }

  get checklistProgress(): number {
    if (this.totalChecklistItems === 0) return 0;
    return Math.round((this.completedChecklistItems / this.totalChecklistItems) * 100);
  }

  get allChecklistsComplete(): boolean {
    return this.totalChecklistItems > 0 && this.completedChecklistItems === this.totalChecklistItems;
  }

  // --- Status ---
  getStatusClass(status: MaintenanceStatus): string {
    switch (status) {
      case MaintenanceStatus.Planned: return 'status-planned';
      case MaintenanceStatus.InProgress: return 'status-progress';
      case MaintenanceStatus.Completed: return 'status-completed';
      case MaintenanceStatus.Overdue: return 'status-overdue';
      case MaintenanceStatus.Cancelled: return 'status-cancelled';
      default: return '';
    }
  }

  getStatusLabel(status: MaintenanceStatus): string {
    if (!this.isPlanned) {
      switch (status) {
        case MaintenanceStatus.Planned: return 'Aktiv';
        case MaintenanceStatus.InProgress: return 'Aktiv';
        case MaintenanceStatus.Completed: return 'Abgeschlossen';
        case MaintenanceStatus.Overdue: return 'Überfällig';
        case MaintenanceStatus.Cancelled: return 'Storniert';
        default: return status;
      }
    }
    switch (status) {
      case MaintenanceStatus.Planned: return 'Geplant';
      case MaintenanceStatus.InProgress: return 'In Arbeit';
      case MaintenanceStatus.Completed: return 'Erledigt';
      case MaintenanceStatus.Overdue: return 'Überfällig';
      case MaintenanceStatus.Cancelled: return 'Storniert';
      default: return status;
    }
  }

  getTechnicianNames(): string {
    const users = this.maintenance?.assignedUsers;
    if (!users?.length) return '–';
    return users.map(u => u.userName).join(', ');
  }

  getRecurrenceLabel(type: MaintenanceRecurrenceType): string {
    switch (type) {
      case MaintenanceRecurrenceType.Daily: return 'Täglich';
      case MaintenanceRecurrenceType.Weekly: return 'Wöchentlich';
      case MaintenanceRecurrenceType.Monthly: return 'Monatlich';
      case MaintenanceRecurrenceType.Quarterly: return 'Vierteljährlich';
      case MaintenanceRecurrenceType.Yearly: return 'Jährlich';
      default: return 'Einmalig';
    }
  }

  // --- Checklist Management ---
  addChecklist(): void {
    if (!this.newChecklistName.trim() || !this.maintenance) return;
    this.maintenanceService.createChecklist({
      maintenanceId: this.maintenance.id,
      name: this.newChecklistName.trim(),
      isTemplate: false,
      items: []
    } as any).subscribe({
      next: (created) => {
        if (this.maintenance) {
          this.maintenance.checklists = this.maintenance.checklists ?? [];
          if (!created.items) created.items = [];
          this.maintenance.checklists.push(created);
        }
        this.showNewChecklist = false;
        this.newChecklistName = '';
      },
      error: () => this.snackBar.open('Fehler beim Erstellen', 'OK', { duration: 3000 })
    });
  }

  deleteChecklist(cl: MaintenanceChecklist): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '380px',
      data: { title: 'Checkliste löschen', message: `"${cl.name}" wirklich löschen?` }
    });
    dialogRef.afterClosed().subscribe(r => {
      if (r) {
        this.maintenanceService.deleteChecklist(cl.id).subscribe({
          next: () => this.reload(),
          error: () => this.snackBar.open('Fehler', 'OK', { duration: 3000 })
        });
      }
    });
  }

  /** Namen einer Checkliste ändern. */
  renameChecklist(cl: MaintenanceChecklist): void {
    const name = window.prompt('Neuer Name der Checkliste:', cl.name ?? '');
    if (name === null) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed === cl.name) return;
    // Nur ein minimales Objekt senden (ohne items): das Backend ändert nur den
    // Namen. Würde man die items mitschicken, schlägt die Model-Validierung fehl
    // (ChecklistItem.MaintenanceChecklist ist eine Pflicht-Navigation).
    const payload = {
      id: cl.id,
      maintenanceId: cl.maintenanceId,
      isTemplate: cl.isTemplate,
      name: trimmed,
      items: []
    } as MaintenanceChecklist;
    this.maintenanceService.updateChecklist(cl.id, payload).subscribe({
      next: () => {
        cl.name = trimmed;
        this.snackBar.open('Checkliste umbenannt', 'OK', { duration: 2000 });
      },
      error: () => this.snackBar.open('Umbenennen fehlgeschlagen', 'OK', { duration: 3000 })
    });
  }

  startAddingItem(checklistId: number): void {
    this.activeChecklistId = checklistId;
    this.newItemText = '';
  }

  cancelAddItem(): void {
    this.activeChecklistId = null;
    this.newItemText = '';
  }

  addChecklistItem(checklistId: number): void {
    if (!this.newItemText.trim()) return;
    // Neuen Punkt ans Ende setzen (Server berechnet zur Sicherheit ebenfalls).
    const cl = this.checklists.find(c => c.id === checklistId);
    const nextOrder = cl && cl.items?.length
      ? Math.max(...cl.items.map(i => i.sortOrder ?? 0)) + 1
      : 0;
    this.maintenanceService.createChecklistItem({
      maintenanceChecklistId: checklistId,
      description: this.newItemText.trim(),
      isChecked: false,
      sortOrder: nextOrder
    } as ChecklistItem).subscribe({
      next: (created) => {
        // Lokal anhängen statt die ganze Seite neu zu laden.
        const target = this.checklists.find(c => c.id === checklistId);
        if (target) {
          target.items = target.items ?? [];
          target.items.push(created);
        }
        this.newItemText = '';
      },
      error: () => this.snackBar.open('Fehler', 'OK', { duration: 3000 })
    });
  }

  /** Verschiebt einen Prüfpunkt um eine Position nach oben/unten und speichert die Reihenfolge. */
  moveChecklistItem(cl: MaintenanceChecklist, item: ChecklistItem, direction: -1 | 1): void {
    const items = (cl.items ?? []).slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    const index = items.findIndex(i => i.id === item.id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= items.length) return;

    // Im UI tauschen
    [items[index], items[target]] = [items[target], items[index]];
    // SortOrder neu vergeben (0..n) und betroffene Items persistieren
    const changed: ChecklistItem[] = [];
    items.forEach((it, i) => {
      if ((it.sortOrder ?? 0) !== i) {
        it.sortOrder = i;
        changed.push(it);
      }
    });
    cl.items = items;

    changed.forEach(it => {
      this.maintenanceService.updateChecklistItem(it.id, {
        id: it.id,
        maintenanceChecklistId: it.maintenanceChecklistId,
        description: it.description,
        isChecked: it.isChecked,
        isNotOk: it.isNotOk ?? false,
        remarks: it.remarks ?? null,
        sortOrder: it.sortOrder
      } as ChecklistItem).subscribe({
        error: () => this.snackBar.open('Reihenfolge konnte nicht gespeichert werden', 'OK', { duration: 3000 })
      });
    });
  }

  toggleChecklistItem(item: ChecklistItem): void {
    if (this.isCompleted) return;
    const prevChecked = item.isChecked;
    const prevNotOk = item.isNotOk ?? false;
    // Optimistisch umschalten ("OK" hebt "Nicht OK" auf), bei Fehler zurücksetzen.
    item.isChecked = !item.isChecked;
    if (item.isChecked) item.isNotOk = false;
    this.maintenanceService.updateChecklistItem(item.id, { ...item }).subscribe({
      next: () => {
        // Fortschritts-Update gebündelt (debounced) auslösen.
        this.progressUpdate$.next();
      },
      error: () => {
        item.isChecked = prevChecked; // revert
        item.isNotOk = prevNotOk;
        this.snackBar.open('Fehler', 'OK', { duration: 3000 });
      }
    });
  }

  /** Markiert einen Prüfpunkt als "Nicht OK" (bzw. hebt die Markierung wieder auf). */
  toggleChecklistItemNotOk(item: ChecklistItem): void {
    if (this.isCompleted) return;
    const prevChecked = item.isChecked;
    const prevNotOk = item.isNotOk ?? false;
    item.isNotOk = !prevNotOk;
    if (item.isNotOk) item.isChecked = false;
    this.maintenanceService.updateChecklistItem(item.id, { ...item }).subscribe({
      next: () => {
        this.progressUpdate$.next();
      },
      error: () => {
        item.isChecked = prevChecked;
        item.isNotOk = prevNotOk;
        this.snackBar.open('Fehler', 'OK', { duration: 3000 });
      }
    });
  }

  deleteChecklistItem(item: ChecklistItem): void {
    this.maintenanceService.deleteChecklistItem(item.id).subscribe({
      next: () => {
        // Lokal entfernen statt komplett neu zu laden.
        const target = this.checklists.find(c => (c.items ?? []).some(i => i.id === item.id));
        if (target && target.items) {
          target.items = target.items.filter(i => i.id !== item.id);
        }
        this.progressUpdate$.next();
      },
      error: () => this.snackBar.open('Fehler', 'OK', { duration: 3000 })
    });
  }

  saveItemRemarks(item: ChecklistItem): void {
    this.maintenanceService.updateChecklistItem(item.id, {
      id: item.id,
      maintenanceChecklistId: item.maintenanceChecklistId,
      description: item.description,
      isChecked: item.isChecked,
      isNotOk: item.isNotOk ?? false,
      remarks: item.remarks ?? null,
      sortOrder: item.sortOrder ?? 0
    } as ChecklistItem).subscribe({
      error: () => this.snackBar.open('Bemerkung konnte nicht gespeichert werden', 'OK', { duration: 3000 })
    });
  }

  getCheckedCount(cl: MaintenanceChecklist): number {
    return (cl.items ?? []).filter(i => i.isChecked || i.isNotOk).length;
  }

  getChecklistCompletionPercent(cl: MaintenanceChecklist): number {
    const items = cl.items ?? [];
    if (items.length === 0) return 0;
    return Math.round((items.filter(i => i.isChecked || i.isNotOk).length / items.length) * 100);
  }

  // --- Material Management ---
  addMaterial(): void {
    if (!this.maintenance || !this.newMaterial.name?.trim()) return;
    this.maintenanceService.addMaterial(this.maintenance.id, {
      name: this.newMaterial.name!,
      quantity: this.newMaterial.quantity ?? 1,
      unit: this.newMaterial.unit ?? 'Stk',
      unitPrice: this.newMaterial.unitPrice ?? 0,
      description: this.newMaterial.description,
      isPacked: false
    }).subscribe({
      next: () => {
        this.showMaterialForm = false;
        this.newMaterial = { name: '', quantity: 1, unit: 'Stk', unitPrice: undefined };
        this.reload();
      },
      error: () => this.snackBar.open('Fehler', 'OK', { duration: 3000 })
    });
  }

  deleteMaterial(mat: MaintenanceMaterial): void {
    if (!this.maintenance) return;
    this.maintenanceService.deleteMaterial(this.maintenance.id, mat.id).subscribe({
      next: () => this.reload(),
      error: () => this.snackBar.open('Fehler', 'OK', { duration: 3000 })
    });
  }

  // --- Status Changes ---
  startMaintenance(): void {
    if (!this.maintenance) return;
    this.maintenanceService.updateMaintenance(this.maintenance.id, {
      ...this.maintenance,
      status: MaintenanceStatus.InProgress,
      startedOn: new Date().toISOString()
    }).subscribe({
      next: () => {
        this.snackBar.open('Wartung gestartet', 'OK', { duration: 2000 });
        this.reload();
      },
      error: (err) => {
        console.error('startMaintenance error', err);
        const msg = err?.error || 'Fehler beim Starten der Wartung';
        this.snackBar.open(msg, 'OK', { duration: 4000 });
      }
    });
  }

  completeMaintenance(): void {
    if (!this.maintenance) return;
    this.maintenanceService.updateMaintenance(this.maintenance.id, {
      ...this.maintenance,
      status: MaintenanceStatus.Completed,
      completedOn: new Date().toISOString(),
      progressPercent: 100
    }).subscribe({
      next: () => {
        this.snackBar.open('Wartung abgeschlossen', 'OK', { duration: 2000 });
        this.reload();
      },
      error: (err) => {
        console.error('completeMaintenance error', err);
        const msg = err?.error || 'Fehler beim Abschließen der Wartung';
        this.snackBar.open(msg, 'OK', { duration: 4000 });
      }
    });
  }

  reopenMaintenance(): void {
    if (!this.maintenance) return;
    this.maintenanceService.updateMaintenance(this.maintenance.id, {
      ...this.maintenance,
      status: MaintenanceStatus.InProgress,
      completedOn: undefined,
      progressPercent: 50
    }).subscribe({
      next: () => {
        this.snackBar.open('Wartung wiedergeöffnet', 'OK', { duration: 2000 });
        this.reload();
      },
      error: (err) => {
        console.error('reopenMaintenance error', err);
        const msg = err?.error || 'Fehler beim Wiederöffnen';
        this.snackBar.open(msg, 'OK', { duration: 4000 });
      }
    });
  }

  // --- Report ---
  generateReport(): void {
    if (!this.maintenance) return;
    const isMobile = window.innerWidth <= 600;
    const dialogRef = this.dialog.open(ServiceReportDialogComponent, {
      width: isMobile ? '100vw' : '800px',
      maxWidth: isMobile ? '100vw' : '90vw',
      maxHeight: '95vh',
      panelClass: isMobile ? 'mobile-report-dialog' : '',
      data: {
        maintenance: this.maintenance,
        customer: this.customer,
        isPlanned: this.isPlanned,
        durationHours: this.durationHours,
        durationMinutes: this.durationMinutes,
        workItems: this.workItems,
        description: this.serviceDescription,
        photos: this.photos,
        signatureDataUrl: this.signatureDataUrl,
        customerSigneeName: this.customerSigneeName,
        arrivalTime: this.arrivalTime,
        departureTime: this.departureTime,
        travelHours: this.travelHours,
        travelMinutes: this.travelMinutes,
        departureFromOffice: this.departureFromOffice,
        returnToOffice: this.returnToOffice,
        initialSituation: this.initialSituation,
        problems: this.problems,
        techSigneeName: this.techSigneeName,
        techSignatureDataUrl: this.techSignatureDataUrl,
        regieLines: this.regieLines
      }
    });

    dialogRef.afterClosed().subscribe((result?: { generatedPdf?: { fileName: string; pdfBase64: string } }) => {
      if (!result?.generatedPdf || !this.maintenance) {
        return;
      }

      const generatedPdf = result.generatedPdf;
      this.latestGeneratedPdfBase64 = generatedPdf.pdfBase64;

      // PDF was already uploaded by the dialog – refresh stored state from server
      this.loadLatestServerReportPdf(this.maintenance.id);
      this.loadReportVersions(this.maintenance.id);
    });
  }

  /** Lädt alle serverseitig gespeicherten Report-Versionen. */
  private loadReportVersions(maintenanceId: number): void {
    this.maintenanceService.getServiceCallReportPdfs(maintenanceId).subscribe({
      next: list => this.reportVersions = list ?? [],
      error: () => this.reportVersions = []
    });
  }

  openReportVersion(report: ServiceCallReportPdfDto): void {
    if (report?.url) {
      window.open(report.url, '_blank');
    }
  }

  /** Einsatz im einheitlichen Formular bearbeiten (gleiche Oberfläche wie Anlegen). */
  editServiceCall(): void {
    if (this.maintenance) {
      this.router.navigate(['/wartung/einsatz-bearbeiten', this.maintenance.id]);
    }
  }

  /** Grunddaten einer geplanten Wartung bearbeiten (Titel, Datum, Anlage, Intervall, Techniker …). */
  editPlannedMaintenance(): void {
    if (!this.maintenance || !this.customer) return;
    const dialogRef = this.dialog.open(MaintenanceFormComponent, {
      width: '640px',
      data: {
        customerId: this.customer.id,
        customerName: this.customer.name,
        equipments: this.customer.equipments ?? [],
        users: this.users,
        type: 'planned',
        maintenance: this.maintenance
      }
    });
    dialogRef.afterClosed().subscribe(r => { if (r) this.reload(); });
  }

  // --- Service Call: Photos ---
  get photos(): { dataUrl: string; name: string }[] {
    if (this.serverPhotos.length > 0) {
      return this.serverPhotos.map(photo => ({
        dataUrl: this.toAbsolutePhotoUrl(photo.url),
        name: photo.fileName
      }));
    }

    return (this.serviceCallData?.photos ?? [])
      .map(photo => ({
        dataUrl: photo.dataUrl ?? (photo.url ? this.toAbsolutePhotoUrl(photo.url) : ''),
        name: photo.name
      }))
      .filter(photo => !!photo.dataUrl);
  }

  get signatureDataUrl(): string | null {
    return this.serviceCallData?.signatureDataUrl ?? this.maintenance?.customerSignatureDataUrl ?? null;
  }

  get customerSigneeName(): string {
    return this.serviceCallData?.customerSigneeName ?? this.maintenance?.customerSigneeName ?? '';
  }

  get serviceDescription(): string {
    return this.serviceCallData?.description || this.maintenance?.notes || '';
  }

  get workItems(): string[] {
    return this.serviceCallData?.workItems ?? this.maintenance?.workItems ?? [];
  }

  get initialSituation(): string {
    return this.serviceCallData?.initialSituation || this.maintenance?.initialSituation || '';
  }

  get problems(): ProblemEntry[] {
    return this.serviceCallData?.problems ?? this.maintenance?.problems ?? [];
  }

  get techSigneeName(): string {
    return this.serviceCallData?.techSigneeName ?? this.maintenance?.techSigneeName ?? '';
  }

  get techSignatureDataUrl(): string | null {
    return this.serviceCallData?.techSignatureDataUrl ?? this.maintenance?.techSignatureDataUrl ?? null;
  }

  get durationText(): string {
    const h = this.durationHours || 0;
    const m = this.durationMinutes || 0;
    if (h === 0 && m === 0) return '–';
    return `${h}h ${m}min`;
  }

  get travelTimeText(): string {
    const h = this.travelHours || 0;
    const m = this.travelMinutes || 0;
    if (h === 0 && m === 0) return '–';
    return `${h}h ${m}min`;
  }

  get regieText(): string {
    if (this.regieLines.length > 0) {
      return `${this.getRegieHoursTotal(this.regieLines).toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}h · ${this.getRegieTotal(this.regieLines).toLocaleString('de-AT', { style: 'currency', currency: 'EUR' })}`;
    }
    const h = this.regieHours || 0;
    const m = this.regieMinutes || 0;
    if (h === 0 && m === 0) return '–';
    return `${h}h ${m}min`;
  }

  get regieLines(): MaintenanceRegieLine[] {
    return this.maintenance?.regieLines?.length
      ? this.maintenance.regieLines
      : this.serviceCallData?.regieLines ?? [];
  }

  get regieAcknowledged(): boolean {
    return this.regieLines.length > 0 && this.regieLines.every(line => !!line.isCustomerAcknowledged);
  }

  get hasOpenRegieLines(): boolean {
    return this.regieLines.length > 0 && !this.regieAcknowledged;
  }

  get customerSignatureButtonIcon(): string {
    if (!this.signatureDataUrl) return 'add';
    return this.hasOpenRegieLines ? 'how_to_reg' : 'edit';
  }

  get customerSignatureButtonLabel(): string {
    if (!this.signatureDataUrl) return 'Unterschrift hinzufügen';
    return this.hasOpenRegieLines ? 'Zustimmung erneut einholen' : 'Unterschrift ändern';
  }

  getRegieTotal(lines: MaintenanceRegieLine[]): number {
    return lines.reduce((sum, line) => sum + ((Number(line.hours) || 0) * (Number(line.hourlyRate) || 0)), 0);
  }

  get materialTotal(): number {
    return this.materials.reduce(
      (sum, mat) =>
        sum +
        ((Number(mat.quantity) || 0) * (Number(mat.unitPrice) || 0)),
      0
    );
  }

  get totalCost(): number {
    return this.getRegieTotal(this.regieLines) + this.materialTotal;
  }

  getRegieHoursTotal(lines: MaintenanceRegieLine[]): number {
    return lines.reduce((sum, line) => sum + (Number(line.hours) || 0), 0);
  }

  private cloneRegieLines(lines: MaintenanceRegieLine[], customerAcknowledged = false): MaintenanceRegieLine[] {
    return lines.map(line => ({
      ...line,
      isCustomerAcknowledged: customerAcknowledged ? true : !!line.isCustomerAcknowledged
    }));
  }

  private syncLocalRegieLines(lines: MaintenanceRegieLine[]): void {
    if (!this.maintenance) return;
    if (!this.serviceCallData) {
      this.serviceCallData = {};
    }

    this.serviceCallData.regieLines = this.cloneRegieLines(lines);
    try {
      localStorage.setItem(`service-call-${this.maintenance.id}`, JSON.stringify(this.serviceCallData));
    } catch { /* ignore */ }
  }

  private toInputDate(value: string | Date | undefined | null): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
  }

  private toScheduledIso(value: string): string {
    const dateValue = value?.trim() || this.toInputDate(this.maintenance?.scheduledDate) || new Date().toISOString().slice(0, 10);
    return new Date(`${dateValue}T08:00:00`).toISOString();
  }

  openPhoto(index: number): void {
    this.selectedPhotoIndex = index;
  }

  closePhotoViewer(): void {
    this.selectedPhotoIndex = null;
  }

  nextPhoto(): void {
    if (this.selectedPhotoIndex !== null && this.selectedPhotoIndex < this.photos.length - 1) {
      this.selectedPhotoIndex++;
    }
  }

  prevPhoto(): void {
    if (this.selectedPhotoIndex !== null && this.selectedPhotoIndex > 0) {
      this.selectedPhotoIndex--;
    }
  }

  // --- Service Call: PDF Download / Versand (nutzt den am Server gespeicherten Bericht) ---
  /**
   * Dateiname für den Download. Ersetzt ein am Ende stehendes `_yyyy-MM-dd`
   * durch das Einsatzdatum – der gespeicherte Name trägt den Erstellungstag,
   * der meist dem Abschlusstag entspricht.
   */
  private buildReportFileName(gespeicherterName: string): string {
    if (!this.maintenance?.scheduledDate) {
      return gespeicherterName;
    }

    const d = new Date(this.maintenance.scheduledDate);
    if (isNaN(d.getTime())) {
      return gespeicherterName;
    }

    const datum = `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(2, '0')}-${`${d.getDate()}`.padStart(2, '0')}`;
    const ersetzt = gespeicherterName.replace(/_\d{4}-\d{2}-\d{2}(?=\.pdf$)/i, `_${datum}`);

    // Passt der Name nicht auf das Muster, bleibt er unverändert.
    return ersetzt || gespeicherterName;
  }

  async downloadServicePDF(): Promise<void> {
    if (!this.maintenance) return;

    const storedPdf = this.getStoredServicePdf();
    if (!storedPdf) {
      this.snackBar.open('Bitte zuerst im Report-Dialog ein PDF erstellen', 'OK', { duration: 3000 });
      return;
    }

    try {
      const link = document.createElement('a');
      link.href = storedPdf.serverUrl ? this.toAbsoluteReportUrl(storedPdf.serverUrl) : `data:application/pdf;base64,${storedPdf.pdfBase64}`;
      // Nicht den gespeicherten Namen verwenden: der trägt den Erstellungstag.
      // Bei Server-URLs gewinnt ohnehin das Content-Disposition des Servers,
      // der denselben Namen bildet – hier zählt es für den Base64-Fall.
      link.download = this.buildReportFileName(storedPdf.fileName);
      link.click();
    } catch (e) {
      console.error('PDF generation error:', e);
      this.snackBar.open('PDF konnte nicht erstellt werden', 'OK', { duration: 3000 });
    }
  }

  async sendServicePdfByEmail(): Promise<void> {
    if (!this.maintenance || this.sendingPdfEmail) return;

    const storedPdf = this.getStoredServicePdf();
    if (!storedPdf) {
      this.snackBar.open('Bitte zuerst im Report-Dialog ein PDF erstellen', 'OK', { duration: 3000 });
      return;
    }

    const defaultEmail = this.customer?.contactPersonEmail?.trim() || '';
    const emailInput = window.prompt('Bitte E-Mail-Adresse eingeben:', defaultEmail);
    const email = emailInput?.trim();

    if (!email) return;

    this.sendingPdfEmail = true;

    this.maintenanceService.sendReportEmail(this.maintenance.id, {
      email
    }).subscribe({
      next: () => {
        this.sendingPdfEmail = false;
        this.snackBar.open('PDF wurde per E-Mail versendet', 'OK', { duration: 3500 });
      },
      error: () => {
        this.sendingPdfEmail = false;
        this.snackBar.open('E-Mail-Versand fehlgeschlagen', 'OK', { duration: 3500 });
      }
    });
  }

  // --- Service Call: Status ---
  setServiceCallStatus(status: MaintenanceStatus): void {
    if (!this.maintenance) return;
    const update: any = {
      ...this.maintenance,
      status
    };
    if (status === MaintenanceStatus.Completed) {
      update.completedOn = new Date().toISOString();
      update.progressPercent = 100;
    }
    if (status === MaintenanceStatus.InProgress) {
      update.completedOn = null;
      update.progressPercent = 50;
      update.startedOn = update.startedOn || new Date().toISOString();
    }
    this.maintenanceService.updateMaintenance(this.maintenance.id, update).subscribe({
      next: () => {
        this.snackBar.open('Status aktualisiert', 'OK', { duration: 2000 });
        this.reload();
      }
    });
  }

  // --- Service Call: Add Photos ---
  triggerAddPhoto(): void {
    const input = this.addPhotoInput?.nativeElement ?? this.addPhotoInputPlanned?.nativeElement;
    input?.click();
  }

  onAddPhotos(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || !this.maintenance) return;

    const uploadOps: Promise<any>[] = [];
    for (let i = 0; i < input.files.length; i++) {
      const file = input.files[i];
      uploadOps.push(this.maintenanceService.uploadServiceCallPhoto(this.maintenance.id, file, file.name).toPromise());
    }

    Promise.all(uploadOps)
      .then(() => {
        this.loadPersistedPhotos(this.maintenance!.id);
        this.invalidateStoredServicePdf();
        this.snackBar.open(`${uploadOps.length} Foto(s) hochgeladen`, 'OK', { duration: 2500 });
      })
      .catch(() => {
        this.snackBar.open('Foto-Upload fehlgeschlagen', 'OK', { duration: 3000 });
      });

    input.value = '';
  }

  removePhoto(index: number): void {
    if (!this.maintenance) return;

    const photo = this.serverPhotos[index];
    if (!photo) return;

    this.maintenanceService.deleteServiceCallPhoto(this.maintenance.id, photo.id).subscribe({
      next: () => {
        this.serverPhotos.splice(index, 1);
        this.invalidateStoredServicePdf();
      },
      error: () => {
        this.snackBar.open('Foto konnte nicht gelöscht werden', 'OK', { duration: 3000 });
      }
    });
  }

  private loadPersistedPhotos(maintenanceId: number): void {
    this.maintenanceService.getServiceCallPhotos(maintenanceId).subscribe({
      next: (photos) => {
        this.serverPhotos = photos ?? [];
      },
      error: () => {
        this.serverPhotos = [];
      }
    });
  }

  private toAbsolutePhotoUrl(url: string): string {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
      return url;
    }

    const apiBase = environment.apiBaseUrl.replace(/\/api\/?$/, '');
    return `${apiBase}${url.startsWith('/') ? '' : '/'}${url}`;
  }

  // --- Service Call: Signature editing ---
  openSignatureEditor(): void {
    this.showSignatureEditor = true;
    this.editSigneeName = this.customerSigneeName || '';
    this.sigEdited = false;
    this.sigCanvasInit = false;
    setTimeout(() => this.initSigCanvas(), 150);
  }

  private initSigCanvas(): void {
    if (this.sigCanvasInit || !this.editSigCanvas) return;
    const canvas = this.editSigCanvas.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    this.sigCtx = ctx;
    this.sigCanvasInit = true;

    canvas.addEventListener('mousedown', (e: MouseEvent) => {
      this.sigDrawing = true; const r = canvas.getBoundingClientRect();
      ctx.beginPath(); ctx.moveTo(e.clientX - r.left, e.clientY - r.top);
    });
    canvas.addEventListener('mousemove', (e: MouseEvent) => {
      if (!this.sigDrawing) return; const r = canvas.getBoundingClientRect();
      ctx.lineTo(e.clientX - r.left, e.clientY - r.top); ctx.stroke(); this.sigEdited = true;
    });
    canvas.addEventListener('mouseup', () => this.sigDrawing = false);
    canvas.addEventListener('mouseleave', () => this.sigDrawing = false);
    canvas.addEventListener('touchstart', (e: TouchEvent) => {
      e.preventDefault(); this.sigDrawing = true; const r = canvas.getBoundingClientRect();
      const t = e.touches[0]; ctx.beginPath(); ctx.moveTo(t.clientX - r.left, t.clientY - r.top);
    }, { passive: false });
    canvas.addEventListener('touchmove', (e: TouchEvent) => {
      e.preventDefault(); if (!this.sigDrawing) return; const r = canvas.getBoundingClientRect();
      const t = e.touches[0]; ctx.lineTo(t.clientX - r.left, t.clientY - r.top); ctx.stroke(); this.sigEdited = true;
    }, { passive: false });
    canvas.addEventListener('touchend', () => this.sigDrawing = false);
  }

  clearSigCanvas(): void {
    if (!this.sigCtx || !this.editSigCanvas) return;
    const c = this.editSigCanvas.nativeElement;
    this.sigCtx.clearRect(0, 0, c.width, c.height);
    this.sigEdited = false;
  }

  cancelSignatureEdit(): void {
    if (this.savingSignature) return;
    this.showSignatureEditor = false;
  }

  saveSignature(): void {
    if (!this.maintenance || this.savingSignature) return;
    this.savingSignature = true;
    if (!this.serviceCallData) {
      this.serviceCallData = {};
    }
    let signatureDataUrl = this.serviceCallData.signatureDataUrl ?? this.maintenance.customerSignatureDataUrl ?? null;
    if (this.sigEdited && this.editSigCanvas) {
      signatureDataUrl = this.editSigCanvas.nativeElement.toDataURL('image/png');
      this.serviceCallData.signatureDataUrl = signatureDataUrl;
    }
    const acknowledgedRegieLines = this.cloneRegieLines(this.regieLines, true);
    this.serviceCallData.customerSigneeName = this.editSigneeName;
    this.serviceCallData.regieLines = acknowledgedRegieLines;
    try {
      localStorage.setItem(`service-call-${this.maintenance.id}`, JSON.stringify(this.serviceCallData));
    } catch { /* ignore */ }

    this.maintenanceService.updateMaintenance(this.maintenance.id, {
      ...this.maintenance,
      customerSigneeName: this.editSigneeName || null,
      customerSignatureDataUrl: signatureDataUrl,
      customerSignatureEnabled: true,
      customerAcknowledged: true,
      regieLines: acknowledgedRegieLines
    }).subscribe({
      next: (updated) => {
        this.maintenance = updated;
        this.syncLocalRegieLines(updated.regieLines ?? acknowledgedRegieLines);
        this.savingSignature = false;
        this.showSignatureEditor = false;
        this.invalidateStoredServicePdf();
        this.snackBar.open('Unterschrift gespeichert', 'OK', { duration: 2000 });
      },
      error: () => {
        this.savingSignature = false;
        this.showSignatureEditor = false;
        this.invalidateStoredServicePdf();
        this.snackBar.open('Unterschrift lokal gespeichert, Server-Speichern fehlgeschlagen', 'OK', { duration: 3500 });
      }
    });
  }

  // --- Service Call: Delete ---
  deleteServiceCall(): void {
    if (!this.maintenance) return;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '380px',
      data: { title: 'Einsatz löschen', message: `"${this.maintenance.title}" wirklich löschen?` }
    });
    dialogRef.afterClosed().subscribe(r => {
      if (r && this.maintenance) {
        this.maintenanceService.deleteMaintenance(this.maintenance.id).subscribe({
          next: () => {
            // Clean up localStorage
            try { localStorage.removeItem(`service-call-${this.maintenance!.id}`); } catch { /* ignore */ }
            this.snackBar.open('Einsatz gelöscht', 'OK', { duration: 3000 });
            this.goBack();
          },
          error: () => this.snackBar.open('Fehler beim Löschen', 'OK', { duration: 3000 })
        });
      }
    });
  }


  private getStoredServicePdfKey(): string | null {
    if (!this.maintenance) return null;
    return `service-pdf-${this.maintenance.id}`;
  }

  private storeGeneratedServicePdf(fileName: string, pdfBase64?: string, serverUrl?: string): void {
    const key = this.getStoredServicePdfKey();
    if (!key) return;

    const payload: StoredServicePdf = {
      fileName,
      pdfBase64,
      generatedAt: new Date().toISOString(),
      serverUrl
    };

    try {
      localStorage.setItem(key, JSON.stringify(payload));
      this.hasStoredServicePdf = true;
    } catch {
      this.hasStoredServicePdf = !!serverUrl || !!pdfBase64 || !!this.latestGeneratedPdfBase64;
    }
  }

  private getStoredServicePdf(): StoredServicePdf | null {
    const key = this.getStoredServicePdfKey();
    if (!key) return null;

    const raw = localStorage.getItem(key);
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw) as StoredServicePdf;
      if (!parsed?.fileName || (!parsed?.pdfBase64 && !parsed?.serverUrl)) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  private refreshStoredServicePdfState(): void {
    this.hasStoredServicePdf = !!this.getStoredServicePdf() || !!this.latestGeneratedPdfBase64;
  }

  private loadLatestServerReportPdf(maintenanceId: number): void {
    this.maintenanceService.getLatestServiceCallReportPdf(maintenanceId).subscribe({
      next: (latest) => {
        this.mergeServerReportIntoStoredPdf(latest);
      },
      error: () => {
        this.refreshStoredServicePdfState();
      }
    });
  }

  private mergeServerReportIntoStoredPdf(report: ServiceCallReportPdfDto): void {
    const key = this.getStoredServicePdfKey();
    if (!key) return;

    const existing = this.getStoredServicePdf();
    const merged: StoredServicePdf = {
      fileName: report.fileName,
      pdfBase64: existing?.pdfBase64,
      generatedAt: report.createdAtUtc,
      serverUrl: report.url
    };

    localStorage.setItem(key, JSON.stringify(merged));
    this.hasStoredServicePdf = true;
  }

  private invalidateStoredServicePdf(): void {
    const key = this.getStoredServicePdfKey();
    if (key) {
      localStorage.removeItem(key);
    }
    this.latestGeneratedPdfBase64 = null;
    this.hasStoredServicePdf = false;
  }

  private toAbsoluteReportUrl(url: string): string {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
      return url;
    }

    const apiBase = environment.apiBaseUrl.replace(/\/api\/?$/, '');
    return `${apiBase}${url.startsWith('/') ? '' : '/'}${url}`;
  }

  private async ensurePdfBase64(storedPdf: StoredServicePdf): Promise<string | null> {
    if (this.latestGeneratedPdfBase64?.trim()) {
      return this.latestGeneratedPdfBase64;
    }

    if (storedPdf.pdfBase64?.trim()) {
      return storedPdf.pdfBase64;
    }

    if (!storedPdf.serverUrl) {
      return null;
    }

    try {
      const response = await fetch(this.toAbsoluteReportUrl(storedPdf.serverUrl));
      if (!response.ok) {
        return null;
      }

      const blob = await response.blob();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('PDF conversion failed'));
        reader.readAsDataURL(blob);
      });

      const resolvedBase64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
      this.storeGeneratedServicePdf(storedPdf.fileName, resolvedBase64, storedPdf.serverUrl);
      return resolvedBase64;
    } catch {
      return null;
    }
  }

  // --- Navigation ---
  goBack(): void {
    if (this.customer) {
      const customerTab = this.route.snapshot.queryParamMap.get('customerTab');
      this.router.navigate(['/wartung/kunden', this.customer.id], {
        queryParams: customerTab ? { tab: customerTab } : undefined
      });
    } else {
      this.router.navigate(['/wartung']);
    }
  }
}
